import { useState } from "react";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { assets, HOST_ROUTE } from "../data.ts";
import { buildEventsNav, withUtm, MAIN_ORIGIN } from "../../../config/crossSiteNav.ts";
import { MobileNav } from "./MobileNav.tsx";

interface NavProps {
  scrolled: boolean;
}

const NAV_ITEMS = buildEventsNav();
const GET_STARTED_HREF = withUtm(`${MAIN_ORIGIN}/#lead-form`, "events", "header_cta");

export function Nav({ scrolled }: NavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <>
    <nav
      className={clsx(
        // top offset clears the event announcement bar when shown (--ann-bar-h), 0 otherwise.
        "fixed inset-x-0 top-[var(--ann-bar-h,0px)] z-[200] flex items-center justify-between border-b px-4 sm:px-5 md:px-8 lg:px-12",
        "transition-[background,backdrop-filter,border-color,padding] duration-[450ms] ease-out",
        scrolled
          ? "border-white/[.08] bg-[rgba(10,9,8,.86)] py-3.5 backdrop-blur-xl"
          : "border-transparent bg-transparent py-[22px]",
      )}
    >
      {/* Brand → restomag.com home (unified: Home is always the main site) */}
      <a
        href={withUtm(`${MAIN_ORIGIN}/`, "events", "nav")}
        className="flex flex-none items-center gap-3.5 no-underline"
      >
        <img src={assets.wordmark} alt="RestoMag" className="block h-[19px] w-auto" />
        <span className="h-[19px] w-px bg-white/[.26]" />
        <span className="font-mono text-xs font-medium uppercase tracking-[.24em] text-flame">Events</span>
      </a>

      {/* Desktop menu */}
      <div className="hidden items-center gap-8 lg:flex">
        {NAV_ITEMS.map((item) => {
          const cls = clsx(
            "text-sm font-medium no-underline transition-colors duration-200 hover:text-white",
            item.selfActive ? "text-white" : "text-white/70",
          );
          return item.external ? (
            <a key={item.label} href={item.href} className={cls} aria-current={item.selfActive ? "page" : undefined}>
              {item.label}
            </a>
          ) : (
            <Link key={item.label} to={item.href} className={cls} aria-current={item.selfActive ? "page" : undefined}>
              {item.label}
            </Link>
          );
        })}
      </div>

      {/* Actions: shared Get Started (primary) + Host an Event (secondary) + mobile trigger */}
      <div className="flex flex-none items-center gap-3">
        <a
          href={GET_STARTED_HREF}
          className="hidden items-center gap-2 rounded-[11px] border border-flame bg-flame px-4 py-[11px] text-sm font-bold text-white no-underline transition hover:border-flame-dark hover:bg-flame-dark sm:px-5 md:inline-flex"
        >
          Get Started
        </a>
        <Link
          to={HOST_ROUTE}
          className="hidden items-center gap-2 rounded-[11px] border border-white/[.24] bg-white/[.06] px-4 py-[11px] text-sm font-semibold text-white no-underline transition hover:bg-white/[.14] sm:px-5 lg:inline-flex"
        >
          Host an Event
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>
    </nav>
    <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
